/**
 * Admin Portal sign-in.
 *
 * Google is asked once, at sign-in: the browser is sent to Google, and the
 * code Google returns is handed to the API, which checks the id token and
 * starts a session of its own in an HttpOnly cookie. Every API call then sends
 * that cookie, and the API decides how long the session lasts
 * (`api/src/admin/admin-session.ts`).
 *
 * Google's id token was the bearer token before. It expires an hour after
 * sign-in and Google gives a browser no way to renew it silently, so admins
 * were signed out every hour; the session cookie is renewed by being used.
 */

import { getAdminApiBaseUrl } from "./api-base-url";

const {
  VITE_GOOGLE_CLIENT_ID: CLIENT_ID = "",
  VITE_USE_MOCK_AUTH: USE_MOCK_AUTH,
} = import.meta.env;

const AUTH_ADMIN_BASE_URL = new URL("auth", getAdminApiBaseUrl()).href;

// Signing in locally otherwise means a real Google account in the zymego.com
// Workspace. With USE_MOCK_AUTH the e2e mocks-server (`npm -w e2e run mocks`)
// stands in for Google, so the redirect comes straight back with a code.
const AUTHORITY =
  USE_MOCK_AUTH === "true"
    ? "http://localhost:3100"
    : "https://accounts.google.com";

const CALLBACK_PATH = "/auth/callback";

/** The `state` sent to Google, and the page to return to afterwards. */
const SIGN_IN_REQUEST_KEY = "admin-sign-in-request";

/**
 * Who is signed in, for display only. Kept in local storage so every tab has
 * it; the API only knows the email.
 */
const PROFILE_KEY = "admin-profile";

/**
 * Set in a tab when another tab signs out. The tab then waits for the admin to
 * sign in again: signing in by itself would bring the session straight back
 * through the Google session that is still open.
 */
const SIGNED_OUT_KEY = "admin-signed-out";

type AdminProfile = { email: string; name?: string };

type SignInRequest = { returnTo: string; state: string };

/**
 * Every admin API request goes through here. The custom header is required by
 * the API: it cannot be sent cross-origin without a CORS preflight, which only
 * the portals pass.
 */
function adminRequestInit(init?: RequestInit): RequestInit {
  const headers = new Headers(init?.headers);
  headers.set("X-Admin-Source", "admin-app");

  return { ...init, credentials: "include", headers };
}

function readJson<T>({
  key,
  storage,
}: {
  key: string;
  storage: Storage;
}): T | null {
  try {
    const value = storage.getItem(key);
    return value ? (JSON.parse(value) as T) : null;
  } catch {
    return null;
  }
}

function getAdminProfile(): AdminProfile | null {
  return readJson<AdminProfile>({ key: PROFILE_KEY, storage: localStorage });
}

function storeAdminProfile(profile: AdminProfile | null): void {
  if (profile) {
    localStorage.setItem(PROFILE_KEY, JSON.stringify(profile));
  } else {
    localStorage.removeItem(PROFILE_KEY);
  }
}

function getSignInRequest(): SignInRequest | null {
  return readJson<SignInRequest>({
    key: SIGN_IN_REQUEST_KEY,
    storage: sessionStorage,
  });
}

/**
 * The page to come back to after sign-in, never the auth routes themselves. A
 * sign-in started again from a failed callback keeps the first one's page.
 */
function currentPage(): string {
  const { hash, pathname, search } = globalThis.location;

  if (pathname === CALLBACK_PATH) {
    return getSignInRequest()?.returnTo ?? "/";
  }

  return pathname === "/logout" ? "/" : `${pathname}${search}${hash}`;
}

async function getAuthorizationEndpoint(): Promise<URL> {
  const response = await fetch(`${AUTHORITY}/.well-known/openid-configuration`);

  if (!response.ok) {
    throw new Error(`Sign-in discovery failed (${response.status}).`);
  }

  const { authorization_endpoint: authorizationEndpoint } =
    (await response.json()) as { authorization_endpoint?: unknown };

  if (typeof authorizationEndpoint !== "string") {
    throw new TypeError("Sign-in discovery has no authorization endpoint.");
  }

  return new URL(authorizationEndpoint);
}

let isRedirecting = false;

async function signIn({
  selectAccount = false,
}: { selectAccount?: boolean } = {}): Promise<void> {
  // Concurrent 401s share one redirect.
  if (isRedirecting) {
    return;
  }

  isRedirecting = true;

  let url: URL;

  try {
    url = await getAuthorizationEndpoint();
  } catch (error) {
    isRedirecting = false;
    throw error;
  }

  const request: SignInRequest = {
    returnTo: currentPage(),
    state: crypto.randomUUID(),
  };
  sessionStorage.setItem(SIGN_IN_REQUEST_KEY, JSON.stringify(request));
  sessionStorage.removeItem(SIGNED_OUT_KEY);

  url.search = new URLSearchParams({
    client_id: CLIENT_ID,
    // Only preselects the Workspace; the API is what enforces it.
    hd: "zymego.com",
    redirect_uri: `${globalThis.location.origin}${CALLBACK_PATH}`,
    response_type: "code",
    scope: "openid profile email",
    state: request.state,
    ...(selectAccount ? { prompt: "select_account" } : {}),
  }).toString();

  globalThis.location.assign(url);
}

/**
 * Hands the code from Google's redirect to the API, which sets the session
 * cookie, and returns the page to go back to. A `state` that is not the one
 * this tab sent means the redirect was not started here, so it is refused.
 */
async function completeSignIn(): Promise<string> {
  const parameters = new URLSearchParams(globalThis.location.search);
  const code = parameters.get("code");
  const request = getSignInRequest();

  if (!code || !request || parameters.get("state") !== request.state) {
    throw new Error("The sign-in response does not match a sign-in request.");
  }

  const response = await fetch(
    AUTH_ADMIN_BASE_URL,
    adminRequestInit({
      body: JSON.stringify({ code }),
      headers: { "Content-Type": "application/json" },
      method: "POST",
    }),
  );

  if (response.status === 403) {
    // Retrying the same callback cannot succeed, unlike an exchange that never
    // reached the API, so its state is spent. The page to return to is kept
    // for signing in with another account.
    sessionStorage.setItem(
      SIGN_IN_REQUEST_KEY,
      JSON.stringify({ ...request, state: crypto.randomUUID() }),
    );
    throw new AdminNotAllowedError();
  }

  if (!response.ok) {
    throw new Error(`Admin sign-in failed (${response.status}).`);
  }

  storeAdminProfile((await response.json()) as AdminProfile);
  // Kept until now so a callback whose exchange never reached the API can be
  // retried by reloading it.
  sessionStorage.removeItem(SIGN_IN_REQUEST_KEY);

  return request.returnTo;
}

/**
 * The Google account signed in, but is not on the Admin Portal's allow list.
 */
class AdminNotAllowedError extends Error {
  constructor() {
    super("This Google account is not allowed to use the Admin Portal.");
    this.name = "AdminNotAllowedError";
  }
}

/**
 * Whether the browser has a live session. Only a 401 means no; anything else
 * that goes wrong is thrown, so an API outage does not send the admin round
 * the sign-in loop.
 */
async function hasSession(): Promise<boolean> {
  const response = await fetch(
    `${AUTH_ADMIN_BASE_URL}/session`,
    adminRequestInit(),
  );

  if (response.status === 401) {
    return false;
  }

  if (!response.ok) {
    throw new Error(`Admin session check failed (${response.status}).`);
  }

  const { email } = (await response.json()) as { email: string };

  if (getAdminProfile()?.email !== email) {
    storeAdminProfile({ email });
  }

  return true;
}

/**
 * Ends the session on the API before anything else. If that fails, the cookie
 * still works, so this throws rather than look signed out: going back to the
 * portal would otherwise let the next person in. Starting the next sign-in is
 * left to the caller, which can then tell the two failures apart.
 */
async function signOut(): Promise<void> {
  const response = await fetch(`${AUTH_ADMIN_BASE_URL}/logout`, {
    ...adminRequestInit(),
    method: "POST",
  });

  if (!response.ok) {
    throw new Error(`Admin sign-out failed (${response.status}).`);
  }

  storeAdminProfile(null);
}

/** Whether another tab signed out since this tab last signed in. */
function wasSignedOutElsewhere(): boolean {
  return sessionStorage.getItem(SIGNED_OUT_KEY) !== null;
}

/**
 * The session cookie is shared by every tab, so a sign-out or a switch to
 * another account in one tab changes who the others act as. Their screens and
 * caches belong to the previous admin, so they reload and start over; after a
 * sign-out they wait for the admin to sign in (see `SIGNED_OUT_KEY`).
 */
function emailIn(storedProfile: string | null): string | undefined {
  try {
    return storedProfile
      ? (JSON.parse(storedProfile) as AdminProfile).email
      : undefined;
  } catch {
    return undefined;
  }
}

function reloadOnAccountChange(): () => void {
  const onStorage = (event: StorageEvent) => {
    if (event.key !== PROFILE_KEY) {
      return;
    }

    const email = emailIn(event.newValue);

    if (emailIn(event.oldValue) === email) {
      return;
    }

    if (email) {
      sessionStorage.removeItem(SIGNED_OUT_KEY);
    } else {
      sessionStorage.setItem(SIGNED_OUT_KEY, "true");
    }

    globalThis.location.reload();
  };

  globalThis.addEventListener("storage", onStorage);

  return () => globalThis.removeEventListener("storage", onStorage);
}

/**
 * `fetch` for the admin API. A 401 means the session has ended, so the admin
 * is sent to sign in and brought back to this page; with a Google session
 * still open that takes no input.
 */
// eslint-disable-next-line max-params
async function adminFetch(
  input: RequestInfo | URL,
  init?: RequestInit,
): Promise<Response> {
  const response = await fetch(input, adminRequestInit(init));

  if (response.status === 401) {
    signIn().catch((error: unknown) => {
      console.error("Admin sign-in could not start:", error);
    });
  }

  return response;
}

export {
  adminFetch,
  AdminNotAllowedError,
  completeSignIn,
  getAdminProfile,
  hasSession,
  reloadOnAccountChange,
  signIn,
  signOut,
  wasSignedOutElsewhere,
};
export type { AdminProfile };
